import React from 'react';
import { Box, HStack, Switch, Text, VStack } from '@chakra-ui/react';
import { Dow, SettingsForm } from '@services/engineService';
import { WeekShapeValue } from '@blocks/onboarding/v2/WeekShapeStep';
import { AvailabilityValue } from '@blocks/onboarding/v2/AvailabilityStep';
import { FeasibilityMessages } from '@utils/v2/feasibilityMessages';
import { hasSportDay, toggleSportDay } from '@utils/v2/availability';
import { getWorkoutColor } from '@utils/planTypes';
import { legacySportOf } from '@utils/v2/sportMapping';
import SportAvailabilityCard from './SportAvailabilityCard';

interface Props {
  form: SettingsForm;
  weekShape: WeekShapeValue;
  onWeekShapeChange: (value: WeekShapeValue) => void;
  availability: AvailabilityValue;
  onAvailabilityChange: (value: AvailabilityValue) => void;
  messages: FeasibilityMessages;
}

const SectionTitle: React.FC<{ index: string; subtitle?: string; children: React.ReactNode }> = ({
  index,
  subtitle,
  children,
}) => (
  <VStack align="stretch" gap={1}>
    <HStack gap={2.5} align="baseline">
      <Text fontSize="xl" fontWeight="black" fontStyle="italic" color="gray.600" lineHeight="1">
        {index}
      </Text>
      <Text fontSize="lg" fontWeight="bold" color="gray.900">
        {children}
      </Text>
    </HStack>
    {subtitle && (
      <Text fontSize="sm" color="gray.500">
        {subtitle}
      </Text>
    )}
  </VStack>
);

const OptionRow: React.FC<{ label: string; description?: string; children: React.ReactNode }> = ({
  label,
  description,
  children,
}) => (
  <HStack justifyContent="space-between" gap={4} borderWidth={1} borderColor="gray.200" rounded="xl" p={4}>
    <VStack align="start" gap={0.5}>
      <Text fontWeight="bold" color="gray.900">
        {label}
      </Text>
      {description && (
        <Text fontSize="xs" color="gray.500">
          {description}
        </Text>
      )}
    </VStack>
    {children}
  </HStack>
);

/**
 * PRD 12 §7 — « Ta semaine type » and « Tes disponibilités » folded into one card per sport, so the
 * athlete reads each sport once: how many sessions, and on which days.
 */
export const MigrationDaysBody: React.FC<Props> = ({
  form,
  weekShape,
  onWeekShapeChange,
  availability,
  onAvailabilityChange,
  messages,
}) => {
  const { sportDays } = availability;
  const daysOnly = form.dayMode === 'DAYS_ONLY';
  const bikeN = weekShape.sessionsPerWeek.CYCLING ?? 0;
  const showBrick = !daysOnly && form.hasBricks && bikeN >= 1;
  const openDaysOf = (sportId: string) => sportDays.filter((d) => d.sportId === sportId).length;
  const colorOf = (sportId: string) => getWorkoutColor(legacySportOf(sportId));
  const toggle = (sportId: string) => (dow: Dow) =>
    onAvailabilityChange({ ...availability, sportDays: toggleSportDay(sportDays, sportId, dow) });

  // R37: a beginner plan has no PPG nor enchaînement to offer.
  const showPpg = form.ppg.available && !daysOnly;
  const hasOptions = showPpg || showBrick || form.tests.available;

  return (
    <VStack align="stretch" gap={7}>
      <VStack align="stretch" gap={3}>
        <SectionTitle
          index="1"
          subtitle={
            daysOnly
              ? undefined
              : 'Choisis d’abord ton nombre de séances par semaine dans chaque discipline. Ensuite, sélectionne tous les jours où tu es dispo.'
          }
        >
          {daysOnly ? 'Tes jours dispo' : 'Tes séances et tes jours dispo'}
        </SectionTitle>

        {daysOnly &&
          form.sports.map((sport) => (
            <SportAvailabilityCard
              key={sport.sportId}
              label={sport.label}
              color={colorOf(sport.sportId)}
              count={null}
              openDays={openDaysOf(sport.sportId)}
              isSelected={(dow) => hasSportDay(sportDays, sport.sportId, dow)}
              onToggleDay={toggle(sport.sportId)}
              message={messages.perSport[sport.sportId]}
            />
          ))}

        {!daysOnly &&
          form.sports.map((sport) => {
            const n = weekShape.sessionsPerWeek[sport.sportId] ?? sport.default;
            const off = n === 0;
            return (
              <SportAvailabilityCard
                key={sport.sportId}
                label={sport.label}
                color={colorOf(sport.sportId)}
                count={n}
                openDays={openDaysOf(sport.sportId)}
                hint={off ? `Tu ne feras pas de ${sport.label.toLowerCase()} dans ce plan.` : undefined}
                hideDays={off}
                isSelected={(dow) => hasSportDay(sportDays, sport.sportId, dow)}
                onToggleDay={toggle(sport.sportId)}
                message={messages.perSport[sport.sportId]}
                stepper={
                  form.dayMode === 'ATHLETE'
                    ? {
                        label: sport.label,
                        min: sport.min,
                        max: sport.max,
                        onChange: (value) =>
                          onWeekShapeChange({
                            ...weekShape,
                            sessionsPerWeek: { ...weekShape.sessionsPerWeek, [sport.sportId]: value },
                          }),
                      }
                    : undefined
                }
              />
            );
          })}
      </VStack>

      {hasOptions && (
        <VStack align="stretch" gap={3}>
          <SectionTitle
            index="2"
            subtitle={daysOnly ? undefined : 'Ajoute du renforcement si tu veux, et choisis ton jour d’enchaînement.'}
          >
            Tes options
          </SectionTitle>

          {showPpg && (
            <OptionRow label="PPG" description="Une séance de renforcement par semaine.">
              <Switch.Root
                colorPalette="orange"
                checked={weekShape.includePpg}
                onCheckedChange={(e) => onWeekShapeChange({ ...weekShape, includePpg: e.checked })}
              >
                <Switch.HiddenInput />
                <Switch.Control />
              </Switch.Root>
            </OptionRow>
          )}

          {showPpg && weekShape.includePpg && (
            <SportAvailabilityCard
              label="Jours de PPG"
              color={colorOf(form.ppg.sportId)}
              count={1}
              openDays={openDaysOf(form.ppg.sportId)}
              isSelected={(dow) => hasSportDay(sportDays, form.ppg.sportId, dow)}
              onToggleDay={toggle(form.ppg.sportId)}
              message={messages.perSport[form.ppg.sportId]}
            />
          )}

          {showBrick && (
            <SportAvailabilityCard
              label="Jour d’enchaînement"
              color="gray.900"
              selectedTextColor="white"
              count={null}
              openDays={availability.brickDow ? 1 : 0}
              hint="Certaines semaines, un vélo suivi d’une course à pied le même jour. Ils ne s’ajoutent pas à ta semaine."
              isSelected={(dow) => availability.brickDow === dow}
              onToggleDay={(dow) =>
                onAvailabilityChange({ ...availability, brickDow: availability.brickDow === dow ? undefined : dow })
              }
              message={messages.brick ?? undefined}
            />
          )}

          {form.tests.available && (
            <OptionRow label="Tests de départ" description="Défini au démarrage de ta préparation.">
              <Text fontSize="sm" fontWeight="bold" color="gray.700">
                {weekShape.includeTests ? 'Activés' : 'Désactivés'}
              </Text>
            </OptionRow>
          )}
        </VStack>
      )}

      {messages.global && (
        <Box p={3} bg="orange.50" rounded="lg" borderWidth={1} borderColor="orange.200">
          <Text fontSize="sm" color="orange.800">
            {messages.global}
          </Text>
        </Box>
      )}
    </VStack>
  );
};

export default MigrationDaysBody;
